/**
 * Brand Palette Utilities
 * AX1 | Capital White-Label Branding — Stage 2
 *
 * Pure, deterministic helpers that derive a 50-900 colour palette from a
 * single base hex colour.  Used by BrandThemeProvider to inject
 * --brand-primary-50 … 900 (+ -rgb variants) at runtime.
 *
 * ── ALGORITHM ──────────────────────────────────────────────────────────────
 * 1. Convert the base hex to HSL.
 * 2. For each shade (50, 100, … 900) adjust lightness:
 *      • 50–400  →  increase lightness toward white (lighter tints)
 *      • 500     →  use the base colour's own lightness
 *      • 600–900 →  decrease lightness toward black (darker shades)
 * 3. Saturation is kept with a small per-step damping to avoid neon artefacts
 *    at extreme light/dark ends.
 * 4. Hue is held constant.
 * 5. Convert back to hex / "R G B" channels.
 *
 * The mapping is identical for any input, so palettes are deterministic and
 * stable across renders.
 * ──────────────────────────────────────────────────────────────────────────
 */

// ── Shade stop index ↔ lightness offset map ───────────────────────────────
// Lightness offset is absolute (added to base L); positive = lighter.
// Saturation damping (0-1, applied multiplicatively) prevents neon at extremes.
const SHADE_CONFIG: Record<number, { lOffset: number; sDamping: number }> = {
    50: { lOffset: 45, sDamping: 0.35 },
    100: { lOffset: 38, sDamping: 0.45 },
    200: { lOffset: 29, sDamping: 0.55 },
    300: { lOffset: 20, sDamping: 0.70 },
    400: { lOffset: 10, sDamping: 0.85 },
    500: { lOffset: 0, sDamping: 1.00 },
    600: { lOffset: -8, sDamping: 0.95 },
    700: { lOffset: -16, sDamping: 0.90 },
    800: { lOffset: -24, sDamping: 0.85 },
    900: { lOffset: -32, sDamping: 0.80 },
};

/**
 * Dark-mode shade config.
 *
 * Key difference: stops 50–300 are compressed to very low lightness so that
 * `bg-primary-50/10` renders as a subtle dark overlay, not a milky pastel.
 * Stops 400–500 stay near the base hue; 600–900 push even darker.
 *
 * ── Visual curve (base L ≈ 51) ──
 * Stop:   50   100  200  300  400  500  600  700  800  900
 * L (≈):  15   14   13   17   26   51   43   35   27   19
 */
const DARK_SHADE_CONFIG: Record<number, { lOffset: number; sDamping: number }> = {
    50: { lOffset: -36, sDamping: 0.20 },  // very dark, barely tinted
    100: { lOffset: -37, sDamping: 0.25 },
    200: { lOffset: -38, sDamping: 0.30 },
    300: { lOffset: -34, sDamping: 0.40 },
    400: { lOffset: -25, sDamping: 0.70 },
    500: { lOffset: 0, sDamping: 1.00 },  // base — unchanged
    600: { lOffset: -8, sDamping: 0.95 },
    700: { lOffset: -16, sDamping: 0.90 },
    800: { lOffset: -24, sDamping: 0.85 },
    900: { lOffset: -32, sDamping: 0.80 },
};

export const SHADE_STOPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

export type ShadeStop = (typeof SHADE_STOPS)[number];

export interface PaletteEntry {
    hex: string;   // e.g. "#3b82f6"
    rgb: string;   // e.g. "59 130 246"  (space-separated for CSS rgb())
}

export type Palette = Record<ShadeStop, PaletteEntry>;

// ── Public API ────────────────────────────────────────────────────────────

/**
 * Convert a hex colour to space-separated RGB channels.
 *
 * @param hex  "#RRGGBB" or "#RGB" (case-insensitive, '#' optional)
 * @returns    "R G B"  e.g. "59 130 246", or "0 0 0" for unparseable input
 */
export function hexToRgbChannels(hex: string): string {
    const rgb = parseHex(hex);
    if (!rgb) return '0 0 0';
    return `${rgb[0]} ${rgb[1]} ${rgb[2]}`;
}

/** Options for palette derivation. */
export interface DeriveOptions {
    /** 'light' (default) uses the standard curve; 'dark' compresses high-lightness stops. */
    mode?: 'light' | 'dark';
}

/**
 * Derive a full 50-900 palette from a single base hex colour.
 *
 * @param baseHex  The brand's base colour (e.g. "#2563EB")
 * @param opts     Optional `{ mode: 'dark' }` for dark-optimized curve
 * @returns        `Record<ShadeStop, { hex, rgb }>`
 */
export function derivePaletteFromBase(baseHex: string, opts?: DeriveOptions): Palette {
    const rgb = parseHex(baseHex);
    if (!rgb) {
        // Unparseable → return a neutral grey palette rather than crashing
        return neutralFallback();
    }

    const [h, s, l] = rgbToHsl(...rgb);
    const config = opts?.mode === 'dark' ? DARK_SHADE_CONFIG : SHADE_CONFIG;
    const result = {} as Palette;

    for (const stop of SHADE_STOPS) {
        const cfg = config[stop];
        // Clamp lightness to [2, 98] to avoid pure-black/white
        const newL = Math.min(98, Math.max(2, l + cfg.lOffset));
        // Dampen saturation toward extremes
        const newS = Math.min(100, Math.max(0, s * cfg.sDamping));
        const [r, g, b] = hslToRgb(h, newS, newL);
        result[stop] = {
            hex: toHex(r, g, b),
            rgb: `${r} ${g} ${b}`,
        };
    }

    return result;
}

// ── Internal helpers ──────────────────────────────────────────────────────

function parseHex(hex: string): [number, number, number] | null {
    const cleaned = hex.replace(/^#/, '');
    let r: number, g: number, b: number;

    if (cleaned.length === 3) {
        r = parseInt(cleaned[0] + cleaned[0], 16);
        g = parseInt(cleaned[1] + cleaned[1], 16);
        b = parseInt(cleaned[2] + cleaned[2], 16);
    } else if (cleaned.length === 6) {
        r = parseInt(cleaned.slice(0, 2), 16);
        g = parseInt(cleaned.slice(2, 4), 16);
        b = parseInt(cleaned.slice(4, 6), 16);
    } else {
        return null;
    }

    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
    return [r, g, b];
}

function toHex(r: number, g: number, b: number): string {
    return (
        '#' +
        r.toString(16).padStart(2, '0') +
        g.toString(16).padStart(2, '0') +
        b.toString(16).padStart(2, '0')
    );
}

/**
 * RGB (0-255) → HSL (h 0-360, s 0-100, l 0-100).
 */
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const d = max - min;
    let h = 0;
    const l = (max + min) / 2;

    if (d !== 0) {
        const s = d / (1 - Math.abs(2 * l - 1));
        if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
        else if (max === gn) h = ((bn - rn) / d + 2) * 60;
        else h = ((rn - gn) / d + 4) * 60;
        return [Math.round(h), Math.round(s * 100), Math.round(l * 100)];
    }

    return [0, 0, Math.round(l * 100)];
}

/**
 * HSL (h 0-360, s 0-100, l 0-100) → RGB (0-255).
 */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    const sn = s / 100;
    const ln = l / 100;
    const c = (1 - Math.abs(2 * ln - 1)) * sn;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = ln - c / 2;
    let rn = 0, gn = 0, bn = 0;

    if (h < 60) { rn = c; gn = x; bn = 0; }
    else if (h < 120) { rn = x; gn = c; bn = 0; }
    else if (h < 180) { rn = 0; gn = c; bn = x; }
    else if (h < 240) { rn = 0; gn = x; bn = c; }
    else if (h < 300) { rn = x; gn = 0; bn = c; }
    else { rn = c; gn = 0; bn = x; }

    return [
        Math.round((rn + m) * 255),
        Math.round((gn + m) * 255),
        Math.round((bn + m) * 255),
    ];
}

function neutralFallback(): Palette {
    const result = {} as Palette;
    // Approximate Tailwind slate-like fallback
    const grey = [
        [248, 250, 252], [241, 245, 249], [226, 232, 240], [203, 213, 225],
        [148, 163, 184], [100, 116, 139], [71, 85, 105], [51, 65, 85],
        [30, 41, 59], [15, 23, 42],
    ];
    SHADE_STOPS.forEach((stop, i) => {
        const [r, g, b] = grey[i];
        result[stop] = { hex: toHex(r, g, b), rgb: `${r} ${g} ${b}` };
    });
    return result;
}
